import React, { FunctionComponent, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all_with_count, fetch_all } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';
import { currentPracticeAtom } from '../../../lib/practice';

import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  Alert,
  Avatar,
  Box,
  Tooltip,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';

import {
  Button,
  DataGrid,
  Dt,
  Fab,
  IconButton,
  Page,
  TagChip,
  Typography
} from '../../../components/v2/styled';
import { SearchBox } from '../../../components/v2/SearchBox';

import { User } from '../../../models/User';
import { UserTag } from '../../../models/UserTag';

const { DateTime } = require("luxon");

type Props = {}

export const CoachUsers: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  const [showHint, setShowHint] = useState<boolean>(false);
  let count = 0;
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<string>("id DESC");
  const [search, setSearch] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<number[]>([]);

  const {
    data: objects,
    isLoading: objectsLoading,
    isError: objectsError,
    isSuccess: objectsSuccess,
    refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["coach", "users", (login?.practice_id || currentPractice?.id), {page: page, order: order, search: search, filterStatus: filterStatus}],
    queryFn: () =>
      fetch_all_with_count<User>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'user',
          fields: ['id', 'first_name', 'last_name', 'email', 'last_sign_in_at', 'last_activity', 'created_at', 'birth_date', 'full_fm_links', 'enabled', 'confirmed_at', 'consent_status', 'phone_mobile', 'consent_status', 'last_appointment', 'next_appointment', 'extra_data', 'user_tags'],
          order: order,
          per_page: 20,
          page: page,
          filter: {
            search: search,
            advanced: {
              for_practice: (login?.practice_id || currentPractice?.id),
              tag_ids: filterStatus.join(",")
            }
          },
          practice_id: (login?.practice_id || currentPractice?.id)
        },
        login
      ),
    select: (d) => {
      count = d.count;
      return d.result;
    },
    enabled: !!(login?.practice_id || currentPractice?.id),
  });
  const {
    data: userTags,
    // isLoading: objectLoading,
    // isError: objectError,
    // isSuccess: objectSuccess,
    refetch: userTagsRefetch,
  } = useQuery({
    queryKey: ["user_tags", (login?.practice_id || currentPractice?.id)],
    queryFn: () =>
      fetch_all<UserTag>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'user_tag',
          fields: ['id', 'name', 'colour', 'bg_colour', 'user_count'],
          order: "name ASC",
          per_page: 1000,
          page: 0,
          filter: {
            search: '',
            advanced: {
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          },
          practice_id: (login?.practice_id || currentPractice?.id)
        },
        login
      ),
    enabled: !!(login?.practice_id || currentPractice?.id),
  });








  const columns: GridColDef[] = [
    // { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: t("coach.models.clients.name", "Naam"), width: 200, renderCell: params => <>
      <Typography sx={{
        lineHeight: '52px',
        fontSize: '0.9rem',
        color: (params.row.email.indexOf("accountclosed") > -1 ? theme.palette.secondary.main : 'rgba(0, 0, 0, 0.87)')
      }}>{params.row.name} {!!params.row.extra_data.is_guest && <Tooltip title={t("coach.models.clients.is_guest")}><PersonOffIcon color="primary" sx={{marginBottom: -0.5, height: '20px', width: '20px', marginLeft: 1}} /></Tooltip>}</Typography>
    </> },
    { field: 'email', headerName: t("coach.models.clients.email", "Email"), width: 250 },
    { field: 'phone_mobile', headerName: t("coach.models.clients.phone_mobile", "Telefoon"), width: 200 },
    { field: 'birth_date', headerName: t("coach.models.clients.birth_date", "Geboortedatum"), width: 150, renderCell: params => <>
      {!!params.row.birth_date && <Dt d={params.row.birth_date} f="dd/LL/y" />}
    </> },
    { field: 'created_at', headerName: t("coach.models.clients.created_at", "Aangemaakt"), width: 200, renderCell: params => <>
      <Dt d={params.row.created_at} f="DDD t" />
    </> },
    { field: 'last_activity', headerName: t("coach.models.clients.last_activity", "Laatste activiteit"), sortable: false, width: 200, renderCell: params => <>
      {!!params.row.last_activity && <Typography sx={{lineHeight: '52px', fontSize: '0.9rem'}}>{DateTime.fromISO(params.row.last_activity).toRelative({ locale: i18n.resolvedLanguage || 'nl' })}</Typography>}
    </> },
    { field: 'last_sign_in_at', headerName: t("coach.models.clients.last_sign_in_at", "Laatste login"), sortable: false, width: 200, renderCell: params => <>
      {!!params.row.last_sign_in_at && <Typography sx={{lineHeight: '52px', fontSize: '0.9rem'}}>{DateTime.fromISO(params.row.last_sign_in_at).toRelative({ locale: i18n.resolvedLanguage || 'nl' })}</Typography>}
    </> },
    { field: 'last_appointment', headerName: t("coach.models.clients.last_appointment"), sortable: false, width: 200, renderCell: params => <>
      {!!params.row.last_appointment && <Dt d={params.row.last_appointment} f="DDD t" />}
    </> },
    { field: 'next_appointment', headerName: t("coach.models.clients.next_appointment"), sortable: false, width: 200, renderCell: params => <>
      {!!params.row.next_appointment && <Dt d={params.row.next_appointment} f="DDD t" />}
    </> },
    { field: 'tags', headerName: t("coach.models.clients.user_tags"), sortable: false, width: 400, renderCell: params => <>
        {/* @ts-ignore */}
        {(params.row.user_tags || []).map(tg => <TagChip tag={tg} />)}
    </> },
  ];

  const exampleLine = (is_gray:boolean, stricken:boolean, guest:boolean, disabled:boolean) => {
    return <Box sx={{
      width: '600px',
      padding: 0,
      border: '1px solid transparent',
      borderColor: 'rgba(224, 224, 224, 1)',
      height: '20px',
      backgroundColor: 'white',
      clear: 'both',
      marginBottom: 1,
      color: (!!is_gray ? 'grey' : 'rgba(0, 0, 0, 0.87)')
    }}>
      <Box sx={{
        width: '100px',
        paddingLeft: 0.5,
        borderRight: '1px solid transparent',
        borderColor: 'rgba(224, 224, 224, 1)',
        display: 'inline',
        float: 'left',
        height: '20px'
      }}>
        <Typography sx={{lineHeight: '20px', fontSize: '0.5rem', textDecoration: (!!stricken ? 'line-through !important' : 'none'), color: (!!disabled ? theme.palette.secondary.main : (!!is_gray ? 'grey' : 'rgba(0, 0, 0, 0.87)'))}}>Kool, Bloem {!!guest && <PersonOffIcon color="primary" sx={{marginBottom: -0.5, height: '14px', width: '14px', marginLeft: 0.5}} />}</Typography>
      </Box>
      <Box sx={{
        width: '120px',
        paddingLeft: 0.5,
        borderRight: '1px solid transparent',
        borderColor: 'rgba(224, 224, 224, 1)',
        display: 'inline',
        float: 'left',
        height: '20px'
      }}>
        <Typography sx={{lineHeight: '20px', fontSize: '0.5rem', textDecoration: (!!stricken ? 'line-through !important' : 'none')}}>bloemkool@example.com</Typography>
      </Box>
      <Box sx={{
        width: '100px',
        paddingLeft: 0.5,
        borderRight: '1px solid transparent',
        borderColor: 'rgba(224, 224, 224, 1)',
        display: 'inline',
        float: 'left',
        height: '20px'
      }}>
        <Typography sx={{lineHeight: '20px', fontSize: '0.5rem', textDecoration: (!!stricken ? 'line-through !important' : 'none')}}>00324 12 34 56 78</Typography>
      </Box>
      <Box sx={{
        width: '80px',
        paddingLeft: 0.5,
        borderRight: '1px solid transparent',
        borderColor: 'rgba(224, 224, 224, 1)',
        display: 'inline',
        float: 'left',
        height: '20px'
      }}>
        <Typography sx={{lineHeight: '20px', fontSize: '0.5rem', textDecoration: (!!stricken ? 'line-through !important' : 'none')}}>20/04/1969</Typography>
      </Box>
      <Box sx={{
        width: '100px',
        paddingLeft: 0.5,
        borderRight: '1px solid transparent',
        borderColor: 'rgba(224, 224, 224, 1)',
        display: 'inline',
        float: 'left',
        height: '20px'
      }}>
        <Typography sx={{lineHeight: '20px', fontSize: '0.5rem', textDecoration: (!!stricken ? 'line-through !important' : 'none')}}>30 november 2024 01:27</Typography>
      </Box>
      <Box sx={{
        width: '90px',
        paddingLeft: 0.5,
        display: 'inline',
        float: 'left',
        height: '20px'
      }}>
        <Typography sx={{lineHeight: '20px', fontSize: '0.5rem', textDecoration: (!!stricken ? 'line-through !important' : 'none')}}>2 weken geleden</Typography>
      </Box>
    </Box>;
  }

  return <Page sx={{paddingBottom: '88px'}} title={<>{t("coach.menu.users", "Cliënten")} <HelpOutlineIcon sx={{marginBottom: -0.25, marginLeft: 2, cursor: 'pointer'}} color="primary" onClick={() => {
    setShowHint(!showHint);
  }} /></>}>
    {!!showHint && <Alert severity='info'>
      <Typography html sx={{marginBottom: 2}}>{t("coach.models.clients.hints.usertable.intro")}</Typography>

      <Typography sx={{marginBottom: 1, textDecoration: 'underline'}}>{t("coach.models.clients.hints.usertable.title_grey")}</Typography>
      {exampleLine(true, false, false, false)}
      <Typography html sx={{marginBottom: 2}}>{t("coach.models.clients.hints.usertable.description_grey")}</Typography>

      <Typography sx={{marginBottom: 1, textDecoration: 'underline'}}>{t("coach.models.clients.hints.usertable.title_stricken")}</Typography>
      {exampleLine(false, true, false, false)}
      <Typography html sx={{marginBottom: 2}}>{t("coach.models.clients.hints.usertable.description_stricken")}</Typography>

      <Typography sx={{marginBottom: 1, textDecoration: 'underline'}}>{t("coach.models.clients.hints.usertable.title_guest")}</Typography>
      {exampleLine(false, false, true, false)}
      <Typography html sx={{marginBottom: 2}}>{t("coach.models.clients.hints.usertable.description_guest")}</Typography>

      <Typography sx={{marginBottom: 1, textDecoration: 'underline'}}>{t("coach.models.clients.hints.usertable.title_disabled")}</Typography>
      {exampleLine(false, false, false, true)}
      <Typography html sx={{marginBottom: 2}}>{t("coach.models.clients.hints.usertable.description_disabled")}</Typography>
    </Alert>}
    <SearchBox
      search={search}
      setSearch={setSearch}
      objectCount={count}
      multiTags={(userTags || []).map(mm => {return {id: (mm.id || 0), name: (mm.name || ''), cnt: mm.user_count};})}
      activeTags={filterStatus}
      setActiveTags={setFilterStatus}
      hideableTags
    />
    <DataGrid
      action_count={3}
      hideCount
      actions={(params) => {
        return (<>
          <IconButton title="Open Detail" color="default" path={`/user/${params.row.id}/detail`}>
            <VisibilityIcon />
          </IconButton>
          <IconButton title="Edit" color="default" path={`/user/${params.row.id}`}>
            <EditIcon />
          </IconButton>
          {Object.keys(params.row.full_fm_links).indexOf(currentPractice?.id?.toString() || '') > -1 && <IconButton title="Edit" color="default" onClick={() => {
            window.open(params.row.full_fm_links[currentPractice?.id?.toString() || ''], "_BLANK")
          }}>
            <Avatar sx={{
              width: 22,
              height: 22
            }} src="https://cdn.kangacoders.com/direct/nutrilink_eu/images/vinkje_blauw_groen.png" />
          </IconButton>}
        </>);
      }}
      objectCount={count}
      getRowClassName={(params) => `datagrid-row-enabled-${params.row.enabled} datagrid-row-stricken-${params.row.consent_status?.is_blocked} datagrid-row-higher-${(params.row.user_tags || []).length > 0 ? 'true' : 'false'}`}
      objects={(objects || []).map(u => ({
        ...u,
        name: [u.last_name, u.first_name].join(", "),
        real_enabled: !!u.enabled,
        enabled: !!u.consent_status?.enabled
      }))}
      columns={columns}
      loader={objectsLoading}
      onSortModelChange={(a, b) => {
        let a_0_field:string = a[0].field;
        if (a_0_field === 'name') a_0_field = `last_name ${a[0].sort}, first_name`;
        setOrder([a_0_field, a[0].sort].join(' '));
      }}
      page={page}
      setPage={(p) => {setPage(p);}}
      sx={{
        // "& .MuiDataGrid-cell": {
        //   position: 'relative'
        // }
      }}
    />

    <Fab
      onClick={() => {
        navigate("/user_tags");
      }}
      icon={<LocalOfferIcon />}
      sx={{
        bottom: 88
      }}
    />
    <Fab
      onClick={() => {
        navigate("/user/new", {});
      }}
    />
  </Page>;
}
